<template>
  <div class="free-submission" :class="{ 'free-submission--saving': saving }" :aria-busy="saving ? 'true' : 'false'">
    <QuestionYesNo
      :key="initValue + '-' + renderKey"
      title="Free Submission"
      :question="{ options: options, designator: 'free_submission' }"
      fieldStyle="mb-1"
      :initValue="initValue"
      @input="handleChange"
    />
    <div class="is-size-7 mb-3" dir="rtl" :class="statusClass">
      <p v-if="!value">אין אישור בתוקף</p>
      <template v-else>
        <p v-if="approvedAt">אושר ב: {{ formatDate(approvedAt) }}</p>
        <p v-else title="מועד האישור לא תועד">אושר ב: —</p>
        <p>רלוונטי עד: {{ formatDate(value) }}</p>
        <p v-if="isExpired" class="has-text-weight-bold">פג תוקף האישור</p>
      </template>
    </div>
  </div>
</template>

<script>
import QuestionYesNo from '@/components/QuestionYesNo'

const DATE_FORMAT = 'DD/MM/YYYY HH:mm'
const WARNING_DAYS = 7
// Must match FreeSubmissionWindow::WINDOW in rebase_server (1 calendar month).
const WINDOW_MONTHS = 1
const REFRESH_MS = 60 * 1000

// Free submission ("הגשה חופשית") display + editor.
//
// `value` is the EXPIRY (applicants.free_submission), `approvedAt` is when the
// consent was given (applicants.free_submission_approved_at). The server owns
// both timestamps: the toggle sends only free_submission_consent true/false
// and then re-reads the applicant, so the browser clock is never written.
export default {
  name: 'FreeSubmission',
  components: {
    QuestionYesNo
  },
  props: {
    value: {
      type: [String, Date],
      default: null
    },
    approvedAt: {
      type: [String, Date],
      default: null
    },
    applicantId: {
      type: Number
    }
  },
  data () {
    return {
      options: [
        { id: 'true', text: 'true' },
        { id: 'false', text: 'false' }
      ],
      saving: false,
      renderKey: 0,
      nowTs: Date.now(),
      timer: null
    }
  },
  computed: {
    expiry () {
      return this.value ? this.$moment(this.value) : null
    },
    isExpired () {
      return !!this.expiry && !this.expiry.isAfter(this.$moment(this.nowTs))
    },
    // The toggle reflects whether the consent is in force: an expired window
    // shows "false" so a recruiter renews it with one click on "true".
    initValue () {
      return this.expiry && !this.isExpired ? 'true' : 'false'
    },
    statusClass () {
      if (!this.expiry) return ''
      const now = this.$moment(this.nowTs)
      if (!this.expiry.isAfter(now)) return 'has-text-danger'
      if (!this.expiry.isAfter(now.clone().add(WARNING_DAYS, 'days'))) return 'has-text-warning'
      return 'has-text-success'
    }
  },
  mounted () {
    this.timer = setInterval(() => { this.nowTs = Date.now() }, REFRESH_MS)
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  },
  methods: {
    formatDate (iso) {
      return this.$moment(iso).format(DATE_FORMAT)
    },
    async handleChange (data) {
      if (this.saving) {
        // Ignored click: redraw the toggle from the stored state.
        this.renderKey += 1
        return
      }
      const consent = data.answer === 'true'
      this.saving = true
      try {
        await this.$network_utils.updateApplicant({ applicant_id: this.applicantId, free_submission_consent: consent })
      } catch (e) {
        // Keep the prior state: remount the toggle from the unchanged props.
        this.renderKey += 1
        this.saving = false
        this.$utils.emit('show_toast', { text: 'failed' })
        return
      }
      await this.refresh(consent)
      this.saving = false
      this.$utils.emit('show_toast', { text: 'הצלחה' })
    },
    // Re-read the server-stamped values. If the read itself fails the write
    // already succeeded, so fall back to the same window the server applies.
    async refresh (consent) {
      let expiry = null
      let approvedAt = null
      try {
        const applicant = await this.$network_utils.getApplicant({ id: this.applicantId })
        expiry = applicant.free_submission || null
        approvedAt = applicant.free_submission_approved_at || null
      } catch (e) {
        if (consent) {
          const now = this.$moment()
          approvedAt = now.toISOString()
          expiry = now.clone().add(WINDOW_MONTHS, 'months').toISOString()
        }
      }
      this.nowTs = Date.now()
      this.$emit('input', expiry)
      this.$emit('update:approvedAt', approvedAt)
      // Remount so the toggle always matches the server state.
      this.renderKey += 1
    }
  }
}
</script>

<style scoped>
.free-submission--saving {
  pointer-events: none;
  opacity: 0.6;
}
</style>
